import {
  AutoComplete,
  type AutoCompleteOptionType,
  ErrorMessage,
  Flex,
} from '@/components';
import { handleKeyDown } from '@/utils';
import type { AutoCompleteProps } from 'antd';
import type { CSSProperties, PropsWithChildren, ReactNode } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';

export type AutoCompleteFieldProps<
  T extends FieldValues,
  U extends AutoCompleteOptionType,
> = {
  name: Path<T>;
  label?: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  options: U[];
  required?: boolean;
  hint?: string;
  onSearch: (value: string) => Promise<void>;
  onSelect: (value: string, option?: U) => void;
  hidden?: boolean;
  labelSize?: FieldLabelSize;
  shouldCoexistOnChange?: boolean;
  onChange?: (value: string) => void;
  searchValue?: string;
  loading?: boolean;
  wrapperStyle?: CSSProperties;
  additionalLabelComponent?: ReactNode;
  htmlFor?: string;
} & Pick<
  AutoCompleteProps,
  | 'popupMatchSelectWidth'
  | 'listHeight'
  | 'open'
  | 'onDropdownVisibleChange'
  | 'maxLength'
>;

export const AutoCompleteField = <
  T extends FieldValues,
  U extends AutoCompleteOptionType,
>({
  name,
  label,
  control,
  errors,
  options,
  hint,
  required,
  onSearch,
  onSelect,
  hidden,
  labelSize = 'medium',
  shouldCoexistOnChange = false,
  onChange,
  searchValue,
  popupMatchSelectWidth,
  listHeight = 256,
  loading = false,
  wrapperStyle,
  open,
  onDropdownVisibleChange,
  children,
  additionalLabelComponent,
  maxLength,
  htmlFor,
}: PropsWithChildren<AutoCompleteFieldProps<T, U>>) => {
  return (
    <Form.Item
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
            additionalComponent={additionalLabelComponent}
          />
        )
      }
      htmlFor={htmlFor}
      hidden={hidden}
      style={wrapperStyle}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => {
          const {
            ref: innerRef,
            onChange: fieldOnChange,
            value: fieldValue,
            ...restField
          } = field;
          return (
            <Flex gap={4}>
              <AutoComplete
                open={open}
                onDropdownVisibleChange={onDropdownVisibleChange}
                onSearch={onSearch}
                popupMatchSelectWidth={popupMatchSelectWidth}
                options={options}
                onSelect={onSelect}
                listHeight={listHeight}
                innerRef={innerRef}
                onKeyDown={(e) => {
                  handleKeyDown(e, onSearch);
                }}
                value={searchValue ?? fieldValue}
                onChange={(value) => {
                  if (shouldCoexistOnChange || !onChange) {
                    fieldOnChange(value);
                  }
                  onChange?.(value);
                }}
                loading={loading}
                maxLength={maxLength}
                {...restField}
              />
              {children}
            </Flex>
          );
        }}
      />
      <ErrorMessage errors={errors} name={name} />
    </Form.Item>
  );
};
